import React from 'react';
import { Users, X, Plus, Pencil, Trash2, Check } from 'lucide-react';
import type { RelationshipLink } from '../../../types/Entities';
import { listRelationships, createRelationship, updateRelationship as dbUpdateRelationship, deleteRelationship as dbDeleteRelationship } from '../../../lib/relationships';
import { supabase } from '../../../lib/supabase';
import { listContactsCore, createContactCore } from '../../../lib/contactsCore';
import { loadGoogleMapsPlaces, extractAddressParts, shortAddressLabel } from '../../../lib/googlePlaces';
import { geoErrorMessage } from '../utils/placeUtils';
import { addHistory } from '../../../lib/history';

interface RelationshipsSectionProps {
    contactId: string;
    userId: string;
}

export default function RelationshipsSection({ contactId, userId }: RelationshipsSectionProps) {
    const [relationships, setRelationships] = React.useState<RelationshipLink[]>([]);
    const [showAdd, setShowAdd] = React.useState(false);
    const [newRel, setNewRel] = React.useState<RelationshipLink>({ relatedName: '', relation: '', whereMet: '', whenMet: '', notes: '' });
    // New: multi-person selection for relationships
    const [selectedPeople, setSelectedPeople] = React.useState<Array<{ id?: string; name: string; avatar?: string | null }>>([]);
    const [personQuery, setPersonQuery] = React.useState('');
    const [personFocused, setPersonFocused] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);
    const [suggestions, setSuggestions] = React.useState<Array<{ id: string; name: string; city?: string | null; state?: string | null; country?: string | null; avatar?: string | null }>>([]);
    const relationRef = React.useRef<HTMLInputElement | null>(null);
    // Places autocomplete for whereMet
    const [placeQuery, setPlaceQuery] = React.useState('');
    const [placeSug, setPlaceSug] = React.useState<Array<{ description: string; place_id: string }>>([]);
    const [placesReady, setPlacesReady] = React.useState(false);
    const [placeSession, setPlaceSession] = React.useState<any>(null);
    const [placeFetching, setPlaceFetching] = React.useState(false);
    const [placeError, setPlaceError] = React.useState<string | null>(null);
    const [placeFocused, setPlaceFocused] = React.useState(false);
    // Relation chips + suggestions
    const [relationTags, setRelationTags] = React.useState<string[]>([]);
    const [relationToken, setRelationToken] = React.useState('');
    const [allRelationTags, setAllRelationTags] = React.useState<string[]>([]);
    const [showAllRelationships, setShowAllRelationships] = React.useState(false);

    // Hydrate relationships from DB
    React.useEffect(() => {
        let active = true;
        if (!contactId) return;
        (async () => {
            try {
                const rows = await listRelationships(contactId);
                if (active) setRelationships(rows);
            } catch (e) {
                console.error('Failed to load relationships', e);
            }
        })();
        return () => { active = false; };
    }, [contactId]);

    // Debounced contact search by name (via core)
    React.useEffect(() => {
        let active = true;
        const q = personQuery.trim();
        if (!q) {
            setSuggestions([]);
            return;
        }
        setIsSearching(true);
        const t = setTimeout(async () => {
            try {
                const data = await listContactsCore(supabase as any, { query: q, limit: 6 } as any);
                if (!active) return;
                const mapped = (data || []).map((c: any) => ({
                    id: c.id,
                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                    city: c.city || null,
                    state: c.state || null,
                    country: c.country || null,
                    avatar: c.profile_image_url || c.photo || null,
                }));
                setSuggestions(mapped);
            } catch (e) {
                console.error('Contact search failed:', e);
                if (active) setSuggestions([]);
            } finally {
                active && setIsSearching(false);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [personQuery]);

    // Load Google Places once
    React.useEffect(() => {
        let mounted = true;
        loadGoogleMapsPlaces().then(() => mounted && setPlacesReady(true)).catch(() => mounted && setPlacesReady(false));
        return () => { mounted = false; };
    }, []);

    // Debounced Places Autocomplete (if available)
    React.useEffect(() => {
        let active = true;
        const q = placeQuery.trim();
        if (!placeFocused || !q || q.length < 2 || !placesReady) { setPlaceSug([]); return; }
        const t = setTimeout(() => {
            try {
                const service = new (window as any).google.maps.places.AutocompleteService();
                const req: any = { input: q, types: ['establishment', 'geocode'] };
                if (placeSession) req.sessionToken = placeSession;
                service.getPlacePredictions(req, (preds: any[]) => {
                    if (!active) return;
                    const out = (preds || []).slice(0, 6).map(p => ({ description: p.description as string, place_id: p.place_id as string }));
                    setPlaceSug(out);
                });
            } catch {
                active && setPlaceSug([]);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [placeQuery, placesReady, placeSession, placeFocused]);

    // Load saved relation tag suggestions from localStorage
    React.useEffect(() => {
        try {
            const raw = localStorage.getItem('gv_relation_tags');
            if (raw) setAllRelationTags(JSON.parse(raw));
        } catch { }
    }, []);

    // Initialize relation tags from newRel.relation if present
    React.useEffect(() => {
        const initial = (newRel.relation || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        if (initial.length) setRelationTags(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep relation string synchronized
    React.useEffect(() => {
        setNewRel(prev => ({ ...prev, relation: relationTags.join(', ') }));
    }, [relationTags]);

    const persistAllRelationTags = (tags: string[]) => {
        const uniq = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
        setAllRelationTags(uniq);
        try { localStorage.setItem('gv_relation_tags', JSON.stringify(uniq)); } catch { }
    };

    const addRelationTag = (tag: string) => {
        const t = tag.trim();
        if (!t) return;
        if (relationTags.some(x => x.toLowerCase() === t.toLowerCase())) return;
        const next = [...relationTags, t];
        setRelationTags(next);
        if (!allRelationTags.some(x => x.toLowerCase() === t.toLowerCase())) {
            persistAllRelationTags([...allRelationTags, t]);
        }
        setRelationToken('');
        setTimeout(() => relationRef.current?.focus(), 0);
    };

    const removeRelationTag = (tag?: string) => {
        if (tag) {
            setRelationTags(prev => prev.filter(x => x !== tag));
        } else {
            setRelationTags(prev => prev.slice(0, -1));
        }
        setTimeout(() => relationRef.current?.focus(), 0);
    };

    const addRel = async () => {
        if (selectedPeople.length === 0 && !newRel.relatedName?.trim()) return;
        // Commit any pending token
        if (relationToken.trim()) addRelationTag(relationToken);
        const relationStr = relationTags.join(', ');
        // Build targets: use selectedPeople if present, else fall back to single name
        const targets = selectedPeople.length > 0 ? selectedPeople : [{ name: newRel.relatedName, id: newRel.relatedContactId, avatar: newRel.relatedAvatar }];
        try {
            for (const t of targets) {
                let relatedContactId = t.id as string | undefined;
                let relatedName = t.name;
                let relatedAvatar = (t as any).avatar || null;
                if (!relatedContactId) {
                    const [first, ...rest] = (relatedName || '').split(' ').filter(Boolean);
                    const last = rest.join(' ') || null;
                    const created = await createContactCore(supabase as any, {
                        first_name: first || relatedName,
                        last_name: last,
                    } as any);
                    relatedContactId = (created as any).id;
                }
                const saved = await createRelationship(contactId, { ...newRel, relatedName, relatedContactId, relatedAvatar, relation: relationStr } as RelationshipLink);
                setRelationships(prev => [...prev, saved]);
                // Fire-and-forget history log
                if (contactId) {
                    try {
                        await addHistory({ contact_id: contactId, actor_id: userId || null, action: 'relationship_created', details: { name: relatedName, relatedContactId: relatedContactId || null, relation: relationStr, whereMet: newRel.whereMet, whenMet: newRel.whenMet } });
                        try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
                    } catch { }
                }
            }
        } catch (e) {
            console.error('Failed to save relationship(s)', e);
            return; // abort clearing inputs if save failed
        }
        // Persist learned tags
        if (relationTags.length) persistAllRelationTags([...allRelationTags, ...relationTags]);
        setNewRel({ relatedName: '', relation: '', whereMet: '', whenMet: '', notes: '' });
        setRelationTags([]);
        setRelationToken('');
        setSelectedPeople([]);
        setShowAdd(false);
    };

    const removeRel = (id: string) => setRelationships(prev => prev.filter(r => r.id !== id));

    // Relationship detail modal state
    const [activeRel, setActiveRel] = React.useState<RelationshipLink | null>(null);
    const [activeEdit, setActiveEdit] = React.useState<RelationshipLink | null>(null);
    const [isEditing, setIsEditing] = React.useState(false);

    // Only enable Save when something actually changed
    const isRelDirty = React.useMemo(() => {
        if (!activeRel || !activeEdit) return false;
        const norm = (v?: string | null) => (v ?? '').trim();
        return (
            norm(activeRel.whereMet) !== norm(activeEdit.whereMet) ||
            norm(activeRel.whenMet) !== norm(activeEdit.whenMet) ||
            norm(activeRel.notes) !== norm(activeEdit.notes)
        );
    }, [activeRel, activeEdit]);

    const openRel = (r: RelationshipLink) => {
        setActiveRel(r);
        setActiveEdit({ ...r });
        setIsEditing(false);
    };
    const closeRel = () => { setActiveRel(null); setActiveEdit(null); setIsEditing(false); };
    const saveRel = async () => {
        if (!activeRel || !activeEdit) return;
        if (!isRelDirty) { setIsEditing(false); return; }
        try {
            const before = { whereMet: activeRel.whereMet || null, whenMet: activeRel.whenMet || null, notes: activeRel.notes || null, relation: activeRel.relation || null };
            const updated = await dbUpdateRelationship(activeRel.id!, activeEdit as RelationshipLink);
            setRelationships(prev => prev.map(r => r.id === activeRel.id ? updated : r));
            // Log history with before/after snapshot
            try {
                await addHistory({
                    contact_id: contactId, actor_id: userId || null, action: 'relationship_updated', details: {
                        id: updated.id,
                        name: updated.relatedName,
                        before,
                        after: { whereMet: updated.whereMet || null, whenMet: updated.whenMet || null, notes: updated.notes || null, relation: updated.relation || null }
                    }
                });
                try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
            } catch { }
            closeRel();
        } catch (e) {
            console.error('Failed to update relationship', e);
        }
    };
    const deleteRel = () => {
        if (!activeRel) return;
        const deleted = activeRel;
        dbDeleteRelationship(activeRel.id!).then(async () => {
            removeRel(activeRel.id!);
            try {
                await addHistory({ contact_id: contactId, actor_id: userId || null, action: 'relationship_deleted', details: { id: deleted.id, name: deleted.relatedName, relation: deleted.relation || null } });
                try { (window as any).dispatchEvent(new CustomEvent('gv:refreshHistory', { detail: { contactId } })); } catch { }
            } catch { }
            closeRel();
        }).catch((e) => {
            console.error('Failed to delete relationship', e);
        });
    };

    // Restore handler: listens for a request to open a specific relationship
    React.useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { id?: string } | undefined;
            const id = detail?.id;
            if (!id) return;
            const r = relationships.find(x => x.id === id);
            if (r) {
                openRel(r);
                // Clear any pending return flags now that we've restored
                try {
                    sessionStorage.removeItem('gv:return:pending');
                    sessionStorage.removeItem('gv:return:contactId');
                    sessionStorage.removeItem('gv:return:scroll');
                    sessionStorage.removeItem('gv:return:relationshipId');
                } catch { /* ignore */ }
            }
        };
        window.addEventListener('gv:openRelationship' as any, handler as any);
        return () => window.removeEventListener('gv:openRelationship' as any, handler as any);
    }, [relationships]);

    // Also check sessionStorage on relationships load in case the event fired too early
    React.useEffect(() => {
        try {
            const pending = sessionStorage.getItem('gv:return:pending');
            const retContact = sessionStorage.getItem('gv:return:contactId');
            const relId = sessionStorage.getItem('gv:return:relationshipId');
            if (pending === '1' && retContact === contactId && relId) {
                const r = relationships.find(x => x.id === relId);
                if (r) {
                    openRel(r);
                    sessionStorage.removeItem('gv:return:pending');
                    sessionStorage.removeItem('gv:return:contactId');
                    sessionStorage.removeItem('gv:return:scroll');
                    sessionStorage.removeItem('gv:return:relationshipId');
                }
            }
        } catch { /* ignore */ }
    }, [relationships, contactId]);

    return (
        <>
            <div className="bg-gray-900 rounded-xl">
                <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                    <div className="flex items-center space-x-2">
                        <Users className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-white">Relationships</h3>
                    </div>
                    <button
                        onClick={() => setShowAdd(v => !v)}
                        className="p-2 rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white"
                        aria-label={showAdd ? 'Close' : 'Add Relationship'}
                        title={showAdd ? 'Close' : 'Add'}
                    >
                        {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </button>
                </div>
                <div className="px-5 pb-5 space-y-4">
                    {showAdd && (
                        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 space-y-3">
                            {/* Inline suggestions ABOVE the Person label for mobile friendliness */}
                            {(personFocused || personQuery) && (suggestions.length > 0) && (
                                <div className="mb-3">
                                    <div className="text-xs text-gray-400 mb-1">Suggestions</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {suggestions.map(s => (
                                            <button
                                                key={s.id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    // Allow multi-select people
                                                    setSelectedPeople(prev => prev.some(p => p.id === s.id) ? prev : [...prev, { id: s.id, name: s.name, avatar: s.avatar || null }]);
                                                    // Also set last picked as the primary relatedName for backward compatibility
                                                    setNewRel({ ...newRel, relatedName: s.name, relatedContactId: s.id, relatedAvatar: s.avatar || null });
                                                    setPersonQuery('');
                                                    setPersonFocused(false);
                                                    setTimeout(() => relationRef.current?.focus(), 50);
                                                }}
                                                className="flex items-center gap-2 p-2 rounded-md border border-gray-700 hover:bg-gray-700 text-left"
                                            >
                                                <div className="w-8 h-8 rounded-md bg-gray-700 overflow-hidden flex-shrink-0">
                                                    {s.avatar ? (
                                                        <img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full bg-gray-700" />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm text-gray-200 truncate">{s.name}</div>
                                                    <div className="text-xs text-gray-400 truncate">{[s.city, s.state, s.country].filter(Boolean).join(', ')}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Person</label>
                                <input
                                    value={newRel.relatedName || personQuery}
                                    onChange={(e) => { setPersonQuery(e.target.value); setNewRel({ ...newRel, relatedName: '' }); }}
                                    onFocus={() => setPersonFocused(true)}
                                    onBlur={() => setTimeout(() => setPersonFocused(false), 120)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const name = newRel.relatedName?.trim() || personQuery.trim();
                                            if (name) {
                                                setNewRel(prev => ({ ...prev, relatedName: name }));
                                                setSelectedPeople(prev => prev.some(p => p.name === name) ? prev : [...prev, { name }]);
                                                setPersonFocused(false);
                                                setTimeout(() => relationRef.current?.focus(), 50);
                                            }
                                        }
                                    }}
                                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    placeholder="Search contacts or create new"
                                />
                                {!isSearching && (personQuery || personFocused) && suggestions.length === 0 && (
                                    <div className="mt-1 text-xs text-gray-400">No matches. Press Enter to keep "{personQuery}".</div>
                                )}
                                {selectedPeople.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {selectedPeople.map((p, idx) => (
                                            <span key={(p.id || p.name) + idx} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-900 border border-gray-700 text-gray-200 text-xs">
                                                {p.avatar ? (<img src={p.avatar} alt={p.name} className="w-4 h-4 rounded-full" />) : null}
                                                {p.name}
                                                <button className="ml-1 text-gray-400 hover:text-white" onClick={() => setSelectedPeople(prev => prev.filter((_, i) => i !== idx))}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="relative">
                                <label className="block text-xs text-gray-400 mb-1">Relation</label>
                                <div className="w-full bg-gray-900 border border-gray-700 rounded-md px-2 py-2 flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-gray-600">
                                    {relationTags.map((t) => (
                                        <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-gray-300 text-xs leading-5">{t}</span>
                                    ))}
                                    <input
                                        ref={relationRef}
                                        value={relationToken}
                                        onChange={(e) => setRelationToken(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ',') {
                                                e.preventDefault();
                                                addRelationTag(relationToken);
                                            } else if (e.key === 'Backspace' && !relationToken) {
                                                removeRelationTag();
                                            }
                                        }}
                                        className="flex-1 min-w-[120px] bg-transparent outline-none text-gray-200 placeholder-gray-500"
                                        placeholder={relationTags.length ? '' : 'Friend, introduced by…'}
                                    />
                                </div>
                                {relationToken.trim() && (
                                    <div className="absolute z-50 mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-56 overflow-auto">
                                        {allRelationTags
                                            .filter(t => t.toLowerCase().includes(relationToken.trim().toLowerCase()))
                                            .filter(t => !relationTags.some(x => x.toLowerCase() === t.toLowerCase()))
                                            .slice(0, 6)
                                            .map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => addRelationTag(t)}
                                                    className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-800 last:border-b-0"
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        {allRelationTags.filter(t => t.toLowerCase().includes(relationToken.trim().toLowerCase())).length === 0 && (
                                            <div className="px-3 py-2 text-xs text-gray-500">Press Enter to add "{relationToken.trim()}"</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="relative">
                                <label className="block text-xs text-gray-400 mb-1">Where you met</label>
                                <input
                                    value={placeQuery || newRel.whereMet || ''}
                                    onChange={(e) => { setPlaceQuery(e.target.value); setNewRel(prev => ({ ...prev, whereMet: e.target.value, whereMetMeta: e.target.value ? { provider: 'manual', label: e.target.value } as any : undefined })); }}
                                    onFocus={() => {
                                        try {
                                            const token = new (window as any).google.maps.places.AutocompleteSessionToken();
                                            setPlaceSession(token);
                                        } catch {
                                            setPlaceSession(null);
                                        }
                                    }}
                                    onBlur={() => {
                                        // allow click on suggestion
                                        setTimeout(() => { setPlaceSug([]); setPlaceSession(null); }, 150);
                                    }}
                                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                    placeholder="Venue or city"
                                />
                                <div className="mt-1 flex items-center justify-between">
                                    <div className="text-xs text-red-300 min-h-[1rem]">{placeError || ''}</div>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            setPlaceError(null);
                                            if (!navigator.geolocation) {
                                                setPlaceError('Location not supported');
                                                return;
                                            }
                                            setPlaceFetching(true);
                                            try {
                                                const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                                                    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
                                                });
                                                await loadGoogleMapsPlaces();
                                                const geocoder = new (window as any).google.maps.Geocoder();
                                                geocoder.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any[], status: any) => {
                                                    if (status !== (window as any).google.maps.GeocoderStatus.OK || !results || results.length === 0) {
                                                        setPlaceError('Could not resolve address');
                                                        setPlaceFetching(false);
                                                        return;
                                                    }
                                                    const best = results[0];
                                                    const parts = extractAddressParts(best.address_components);
                                                    const label = shortAddressLabel(null, parts.city || null, parts.state || null, parts.country || null) || best.formatted_address;
                                                    setNewRel(prev => ({
                                                        ...prev,
                                                        whereMet: label,
                                                        whereMetMeta: {
                                                            provider: 'google',
                                                            place_id: best.place_id || null,
                                                            lat: pos.coords.latitude,
                                                            lng: pos.coords.longitude,
                                                            city: parts.city || null,
                                                            state: parts.state || null,
                                                            country: parts.country || null,
                                                            label,
                                                        }
                                                    }));
                                                    setPlaceQuery('');
                                                    setPlaceFocused(false);
                                                    setPlaceFetching(false);
                                                });
                                            } catch (e) {
                                                setPlaceError(geoErrorMessage(e));
                                                setPlaceFetching(false);
                                            }
                                        }}
                                        disabled={placeFetching}
                                        className="text-xs text-blue-300 hover:text-blue-200 underline disabled:opacity-50"
                                    >
                                        {placeFetching ? 'Locating…' : 'Use my location'}
                                    </button>
                                </div>
                                {placesReady && placeFocused && placeQuery.trim() && placeSug.length > 0 && (
                                    <div className="mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-56 overflow-auto">
                                        {placeSug.map(p => (
                                            <button
                                                key={p.place_id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={async () => {
                                                    try {
                                                        const svc = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
                                                        const req: any = { placeId: p.place_id, fields: ['name', 'geometry.location', 'address_components', 'formatted_address'] };
                                                        if (placeSession) req.sessionToken = placeSession;
                                                        svc.getDetails(req, (res: any, status: any) => {
                                                            if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !res) return;
                                                            const loc = res.geometry?.location;
                                                            const parts = extractAddressParts(res.address_components);
                                                            const label = shortAddressLabel(res.name || null, parts.city || null, parts.state || null, parts.country || null) || res.formatted_address || p.description;
                                                            setNewRel(prev => ({
                                                                ...prev,
                                                                whereMet: label,
                                                                whereMetMeta: {
                                                                    provider: 'google',
                                                                    place_id: p.place_id,
                                                                    lat: loc ? loc.lat() : null,
                                                                    lng: loc ? loc.lng() : null,
                                                                    city: parts.city || null,
                                                                    state: parts.state || null,
                                                                    country: parts.country || null,
                                                                    label,
                                                                }
                                                            }));
                                                            setPlaceQuery('');
                                                            setPlaceSug([]);
                                                            setPlaceFocused(false);
                                                        });
                                                    } catch {
                                                        // fallback: keep manual text
                                                    }
                                                }}
                                                className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-800 last:border-b-0"
                                            >
                                                {p.description}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => { setShowAdd(false); setNewRel({ relatedName: '', relation: '', whereMet: '', whenMet: '', notes: '' }); }} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Cancel</button>
                                <button type="button" onClick={addRel} disabled={(selectedPeople.length === 0) && !newRel.relatedName?.trim()} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50">Add Relationship</button>
                            </div>
                        </div>
                    )}
                    {/* Relationships list */}
                    {relationships.length > 0 ? (
                        <div className="space-y-2">
                            {(showAllRelationships ? relationships : relationships.slice(0, 1)).map(r => (
                                <button
                                    key={r.id || r.relatedName + r.whenMet}
                                    onClick={() => openRel(r)}
                                    className="w-full text-left bg-gray-800 rounded-lg p-4 border border-gray-700 hover:bg-gray-700/40 hover:border-gray-600 transition-colors cursor-pointer"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <button
                                            type="button"
                                            title={r.relatedContactId ? 'Open profile' : undefined}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (r.relatedContactId) {
                                                    try {
                                                        const container = document.getElementById('main-scroll-container');
                                                        const pos = container ? (container.scrollTop || 0) : (window.scrollY || 0);
                                                        sessionStorage.setItem('gv:return:pending', '1');
                                                        sessionStorage.setItem('gv:return:contactId', contactId);
                                                        if (r.id) sessionStorage.setItem('gv:return:relationshipId', r.id);
                                                        sessionStorage.setItem('gv:return:scroll', String(pos));
                                                    } catch { /* ignore */ }
                                                    try { window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: r.relatedContactId } } as any)); } catch { }
                                                }
                                            }}
                                            className="w-9 h-9 rounded-md bg-gray-700 overflow-hidden flex-shrink-0 border border-transparent hover:border-gray-600"
                                        >
                                            {r.relatedAvatar ? (
                                                // eslint-disable-next-line @next/next/no-img-element
                                                <img src={r.relatedAvatar} alt={r.relatedName} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-gray-700" />
                                            )}
                                        </button>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <button
                                                    type="button"
                                                    title={r.relatedContactId ? 'Open profile' : undefined}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (r.relatedContactId) {
                                                            try {
                                                                const container = document.getElementById('main-scroll-container');
                                                                const pos = container ? (container.scrollTop || 0) : (window.scrollY || 0);
                                                                sessionStorage.setItem('gv:return:pending', '1');
                                                                sessionStorage.setItem('gv:return:contactId', contactId);
                                                                if (r.id) sessionStorage.setItem('gv:return:relationshipId', r.id);
                                                                sessionStorage.setItem('gv:return:scroll', String(pos));
                                                            } catch { /* ignore */ }
                                                            try { window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: r.relatedContactId } } as any)); } catch { }
                                                        }
                                                    }}
                                                    className="text-left text-white font-medium truncate hover:underline"
                                                >
                                                    {r.relatedName || '—'}
                                                </button>
                                                {r.whenMet ? (
                                                    <div className="text-xs text-gray-400 whitespace-nowrap">{new Date(r.whenMet).toLocaleDateString()}</div>
                                                ) : null}
                                            </div>
                                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                                {r.relation && r.relation.split(',').map(t => t.trim()).filter(Boolean).slice(0, 4).map(tag => (
                                                    <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-900 border border-gray-700 text-gray-300 text-xs">{tag}</span>
                                                ))}
                                                {r.whereMet ? (
                                                    <span className="text-xs text-gray-400 truncate">• {r.whereMet}</span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                    {r.notes ? (
                                        <div className="mt-2 text-sm text-gray-300 line-clamp-2">{r.notes}</div>
                                    ) : null}
                                </button>
                            ))}
                            {relationships.length > 1 && (
                                <div className="pt-1 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowAllRelationships(v => !v)}
                                        className="px-3 py-1.5 text-xs rounded-md border border-transparent bg-gray-900/70 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-700 active:border-gray-700 focus-visible:border-gray-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-600 transition-colors"
                                    >
                                        {showAllRelationships ? 'View less' : `View ${relationships.length - 1} more`}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (!showAdd && (
                        <div className="rounded-lg p-4 border-2 border-dashed border-gray-700 bg-gray-900/40">
                            <p className="text-sm text-gray-400">No relationships yet. Add one to get started.</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Relationship modal */}
            {activeRel && activeEdit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={closeRel} />
                    <div className="relative w-full sm:max-w-lg bg-gray-900 border border-gray-700 rounded-xl p-5 shadow-2xl">
                        {/* Top-left: Delete while editing */}
                        {isEditing && (
                            <div className="absolute top-3 left-3">
                                <button
                                    onClick={() => deleteRel()}
                                    className="p-2 rounded-md border border-red-700 bg-red-900/30 text-red-200 hover:bg-red-900/40 inline-flex items-center gap-2"
                                    title="Delete"
                                    aria-label="Delete"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        {/* Top-right: Edit / Check / Save */}
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                            {!isEditing ? (
                                <button aria-label="Edit" title="Edit" onClick={() => setIsEditing(true)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                    <Pencil className="w-4 h-4" />
                                </button>
                            ) : isRelDirty ? (
                                <button onClick={saveRel} className="px-2.5 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Save changes" aria-label="Save changes">
                                    Save
                                </button>
                            ) : (
                                <button onClick={() => setIsEditing(false)} className="p-2 rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Done" aria-label="Done">
                                    <Check className="w-4 h-4" />
                                </button>
                            )}
                            <button onClick={closeRel} aria-label="Close" title="Close" className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex flex-col items-center text-center">
                            <button
                                type="button"
                                title={activeEdit.relatedContactId ? 'Open profile' : undefined}
                                onClick={() => {
                                    if (activeEdit.relatedContactId) {
                                        try {
                                            const container = document.getElementById('main-scroll-container');
                                            const pos = container ? (container.scrollTop || 0) : (window.scrollY || 0);
                                            sessionStorage.setItem('gv:return:pending', '1');
                                            sessionStorage.setItem('gv:return:contactId', contactId);
                                            if (activeEdit.id) sessionStorage.setItem('gv:return:relationshipId', activeEdit.id);
                                            sessionStorage.setItem('gv:return:scroll', String(pos));
                                        } catch { /* ignore */ }
                                        try { window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: activeEdit.relatedContactId } } as any)); } catch { }
                                        closeRel();
                                    }
                                }}
                                className="w-16 h-16 rounded-md bg-gray-700 overflow-hidden flex-shrink-0 border border-transparent hover:border-gray-600"
                            >
                                {activeEdit.relatedAvatar ? (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img src={activeEdit.relatedAvatar} alt={activeEdit.relatedName} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-gray-700" />
                                )}
                            </button>
                            <button
                                type="button"
                                title={activeEdit.relatedContactId ? 'Open profile' : undefined}
                                onClick={() => {
                                    if (activeEdit.relatedContactId) {
                                        try {
                                            const container = document.getElementById('main-scroll-container');
                                            const pos = container ? (container.scrollTop || 0) : (window.scrollY || 0);
                                            sessionStorage.setItem('gv:return:pending', '1');
                                            sessionStorage.setItem('gv:return:contactId', contactId);
                                            if (activeEdit.id) sessionStorage.setItem('gv:return:relationshipId', activeEdit.id);
                                            sessionStorage.setItem('gv:return:scroll', String(pos));
                                        } catch { /* ignore */ }
                                        try { window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: activeEdit.relatedContactId } } as any)); } catch { }
                                        closeRel();
                                    }
                                }}
                                className="mt-2 text-white font-semibold truncate max-w-[80%] hover:underline"
                            >
                                {activeEdit.relatedName || '—'}
                            </button>
                        </div>

                        {/* Listener to programmatically open a specific relationship when returning */}

                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Relation</label>
                                <input
                                    disabled={!isEditing}
                                    value={activeEdit.relation || ''}
                                    onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), relation: e.target.value }))}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Where met</label>
                                <input
                                    disabled={!isEditing}
                                    value={activeEdit.whereMet || ''}
                                    onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), whereMet: e.target.value }))}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">When met</label>
                                <input
                                    type="date"
                                    disabled={!isEditing}
                                    value={activeEdit.whenMet || ''}
                                    onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), whenMet: e.target.value }))}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Notes</label>
                                <textarea
                                    disabled={!isEditing}
                                    value={activeEdit.notes || ''}
                                    onChange={(e) => setActiveEdit(prev => ({ ...(prev as any), notes: e.target.value }))}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 disabled:opacity-70"
                                    rows={4}
                                />
                            </div>
                            {/* Bottom row removed; actions live in header */}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}